import { createContext, useContext, useEffect, useMemo, useState } from 'react'

import { useCreatePulverization } from '@/hooks/mutations/use-create-pulverization'
import { useGetInfestationFromDiagnosis } from '@/hooks/queries/use-get-infestation-from-diagnosis'
import { useGetPulverizationFromDiagnosis } from '@/hooks/queries/use-get-pulverization-from-diagnosis'
import { useNotification } from '@/hooks/use-notification'
import { HerbicideTreatment } from '@/services/get-herbicide-treatments'
import { Culture } from '@/types/culture'
import { Treatment } from '@/types/treatment'

import { EquipmentFormValue } from './equipment-definition-form'

interface CreatePulverizationMapContextProviderProps {
  treatment: Treatment
  culture: Culture
  diagnosisId?: string
}

const mergeColors: Record<number, string> = {
  0: '#3AECEC',
  1: '#F1369B',
  2: '#01676D',
}

function useCreatePulverizationMapContext({
  culture,
  treatment,
  diagnosisId,
}: CreatePulverizationMapContextProviderProps) {
  const [step, setStep] = useState(0)
  const [intersect, setIntersect] = useState(true)
  const [shouldShowBuffer, setShouldShowBuffer] = useState(false)
  const [canRestartPulverization, setCanRestartPulverization] = useState(true)
  const [hasPulverizationMap, setHasPulverizationMap] = useState(false)
  const [showPulverizationNotification] = useNotification({
    text: 'A atividade dessa pulverização foi criada com sucesso em seu calendário.',
  })

  const infestationFromDemandQuery = useGetInfestationFromDiagnosis(
    culture,
    diagnosisId,
  )
  const createPulverizationMutation = useCreatePulverization(
    culture,
    diagnosisId!,
  )
  const pulverizationFromDemandQuery = useGetPulverizationFromDiagnosis(
    culture,
    diagnosisId!,
    !!infestationFromDemandQuery.data?.id &&
      !createPulverizationMutation.data &&
      hasPulverizationMap,
  )

  const pulverizationData = useMemo(() => {
    const data =
      createPulverizationMutation.data || pulverizationFromDemandQuery.data

    return (
      data && {
        layers: data.layers,
        summary: {
          title: 'Dados da pulverização',
          subtitle: 'Área total',
          head: data.total_area,
          intersect: data.layers.some((layer) => layer.intersect),
          groups: [
            {
              name: 'Pulverização total',
              area: data.total_pulverization_area,
              layers: data.layers.map((layer) => ({
                name: layer.composed_name_layers.join('-'),
                composed_name_layers: layer.composed_name_layers,
                area: layer.area,
                colorPalette: layer.colorPalette,
                buffer: layer.buffer,
                display: layer.display,
                herbicide_treatment: layer.herbicide_treatment,
              })),
            },
          ],
          pulverization_date: data.pulverization_date,
          id: data.id,
        },
      }
    )
  }, [createPulverizationMutation.data, pulverizationFromDemandQuery.data])

  function toggleRemovedAllLayers(
    value: boolean,
    shouldClearMergedLayers = false,
  ) {
    infestationFromDemandQuery.updateLayersFromQuery((draft) => {
      draft.layersMap.forEach((_, index) => {
        const layer = draft.layersMap[index]
        layer.removed = value

        if ((value || shouldClearMergedLayers) && layer.mergedName) {
          layer.mergedName = ''

          if (draft.mergedLayersMap.length) draft.mergedLayersMap = []
        }
      })
    })
  }

  function toggleRemovedLayer(index: number, value: boolean) {
    infestationFromDemandQuery.updateLayersFromQuery((draft) => {
      const currentLayer = draft.layersMap[index]
      currentLayer.removed = value

      if (currentLayer && currentLayer.mergedName) {
        const { mergedName } = currentLayer

        const index = draft.mergedLayersMap.findIndex(
          (mergedLayer) => mergedLayer.name === mergedName,
        )
        if (index !== -1) draft.mergedLayersMap.splice(index, 1)

        draft.layersMap.forEach((layer) => {
          if (layer.mergedName === mergedName) layer.mergedName = ''
        })
      }
    })
  }

  function addToMergeLayers(layersToChange: string[]) {
    const newLayerName = layersToChange.join('-')

    infestationFromDemandQuery.updateLayersFromQuery((draft) => {
      draft.layersMap.forEach((layer) => {
        if (layersToChange.includes(layer.name)) {
          layer.mergedName = newLayerName
        }
      })

      draft.mergedLayersMap.push({
        name: newLayerName,
        colorPalette: mergeColors[draft.mergedLayersMap.length],
        buffer: 10,
        buffered: true,
      })
    })
  }

  function removeFromMergeLayers(index: number) {
    infestationFromDemandQuery.updateLayersFromQuery((draft) => {
      const { name } = draft.mergedLayersMap[index]

      draft.layersMap.forEach((layer) => {
        if (layer.mergedName === name) {
          layer.mergedName = ''
        }
      })

      draft.mergedLayersMap.splice(index, 1)
      draft.mergedLayersMap.forEach((layer, index) => {
        layer.colorPalette = mergeColors[index]
      })
    })
  }

  function applyBufferToLayer(
    list: 'layersMap' | 'mergedLayersMap',
    index: number,
    buffer: number,
  ) {
    infestationFromDemandQuery.updateLayersFromQuery((draft) => {
      draft[list][index].buffer = buffer
    })
  }

  function toggleBufferToLayer(
    list: 'layersMap' | 'mergedLayersMap',
    index: number,
  ) {
    infestationFromDemandQuery.updateLayersFromQuery((draft) => {
      const layer = draft[list][index]
      layer.buffered = !layer.buffered
    })
  }

  function applyHerbicideTreatmentToLayer(
    list: 'layersMap' | 'mergedLayersMap',
    name: string,
    herbicideTreatment?: HerbicideTreatment | null,
  ) {
    infestationFromDemandQuery.updateLayersFromQuery((draft) => {
      const index = draft[list].findIndex((layer) => layer.name === name)
      draft[list][index].herbicideTreatment = herbicideTreatment
    })
  }

  function toggleFillToLayer(layerName: string, value: boolean) {
    infestationFromDemandQuery.updateLayersFromQuery((draft) => {
      draft.layersMap.find((layer) => layer.name === layerName)!.fillColor =
        value
    })
  }

  function showBuffer() {
    setShouldShowBuffer(true)
  }

  function nextStep() {
    setStep((state) => state + 1)
  }

  function previousStep() {
    setStep((state) => state - 1)
  }

  function handleSetStep(value: number) {
    setStep(value)
  }

  function mutateProcessDemandResumeMutation(
    equipmentData: EquipmentFormValue,
  ) {
    const { layersMap, mergedLayersMap } = infestationFromDemandQuery.data!

    createPulverizationMutation.mutate(
      {
        layersMap,
        mergedLayersMap,
        equipmentData,
        intersect,
      },
      {
        onSuccess(data) {
          setStep(6)
          setHasPulverizationMap(true)
          pulverizationFromDemandQuery.setQueryData(data)
          showPulverizationNotification()
        },
      },
    )
  }

  function restartPulverization() {
    setCanRestartPulverization(true)
    setStep(1)
  }

  function resetStep() {
    toggleRemovedAllLayers(false, true)
    setShouldShowBuffer(false)
    setStep(0)
    setIntersect(true)
  }

  const isPulverization = useMemo(() => step > 0, [step])

  useEffect(() => {
    if (infestationFromDemandQuery.data?.status.name === 'pulverization') {
      setCanRestartPulverization(false)
      setHasPulverizationMap(true)
    }
  }, [infestationFromDemandQuery.data?.status.name])

  return {
    step,
    isPulverization,
    shouldShowBuffer,
    infestationData: infestationFromDemandQuery.data,
    pulverizationData,
    hasPulverizationMap,
    canRestartPulverization,
    nextStep,
    previousStep,
    setStep: handleSetStep,
    toggleRemovedAllLayers,
    toggleRemovedLayer,
    addToMergeLayers,
    removeFromMergeLayers,
    applyBufferToLayer,
    toggleBufferToLayer,
    applyHerbicideTreatmentToLayer,
    toggleFillToLayer,
    showBuffer,
    processDemandResumeMutation: {
      mutate: mutateProcessDemandResumeMutation,
      reset: createPulverizationMutation.reset,
      isLoading: createPulverizationMutation.isLoading,
    },
    pulverizationFromDemandQuery: {
      refetch: pulverizationFromDemandQuery.refetch,
      isFetching: pulverizationFromDemandQuery.isFetching,
    },
    restartPulverization,
    resetStep,
    intersect,
    setIntersect,
    culture,
    treatment,
    diagnosisId,
  }
}

const CreatePulverizationMapContext = createContext(
  {} as ReturnType<typeof useCreatePulverizationMapContext>,
)

export function CreatePulverizationMapContextProvider({
  children,
  ...props
}: CreatePulverizationMapContextProviderProps & { children: React.ReactNode }) {
  return (
    <CreatePulverizationMapContext.Provider
      value={useCreatePulverizationMapContext(props)}
    >
      {children}
    </CreatePulverizationMapContext.Provider>
  )
}

export function useCreatePulverizationMap() {
  return useContext(CreatePulverizationMapContext)
}
